import { useState } from "react";
import { Plus, Trash, ArrowRight, Football } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function LeagueManager({ leagues, addLeague, removeLeague, loadLeagueData }) {
  const [leagueId, setLeagueId] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!leagueId.trim()) {
      toast.error("Please enter a Sleeper league ID");
      return;
    }
    setAdding(true);
    const success = await addLeague(leagueId.trim());
    if (success) {
      toast.success("League imported successfully!");
      setLeagueId("");
    } else {
      toast.error("Could not find that league on Sleeper. Check the ID and try again.");
    }
    setAdding(false);
  };

  return (
    <div className="max-w-[1200px] mx-auto px-3 sm:px-6 py-8 sm:py-12">
      {/* Hero Section */}
      <div className="relative overflow-hidden border border-[#262626] bg-[#171717] mb-12" data-testid="league-hero">
        <div
          className="absolute inset-0 opacity-[0.07] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1767584413129-2ad2941b7e46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZvb3RiYWxsJTIwc3RhZGl1bSUyMGxpZ2h0c3xlbnwwfHx8fDE3NzU1MTAwMzN8MA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="relative z-10 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#ccff00] flex items-center justify-center rounded-sm">
              <Football size={24} weight="bold" color="#000" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white">
              DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span> FINDER
            </h1>
          </div>
          <p className="text-[#a3a3a3] text-base max-w-xl mb-8">
            Import your Sleeper dynasty leagues, view real-time player values from FantasyCalc,
            and get AI-powered trade suggestions to dominate your league.
          </p>

          {/* Import Form */}
          <div className="flex items-center gap-3 max-w-lg">
            <Input
              placeholder="Enter Sleeper League ID"
              value={leagueId}
              onChange={(e) => setLeagueId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              className="h-11 bg-[#0a0a0a] border-[#262626] text-white placeholder:text-[#666] font-mono text-sm focus:ring-2 focus:ring-[#ccff00]"
              data-testid="league-id-input"
            />
            <Button
              onClick={handleAdd}
              disabled={adding}
              className="h-11 px-6 btn-primary rounded-sm"
              data-testid="import-league-btn"
            >
              {adding ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Plus size={16} weight="bold" className="mr-1" />
                  IMPORT
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-[#666] mt-3">
            Find your league ID in the Sleeper app under League Settings, or from the URL when viewing your league.
          </p>
        </div>
      </div>

      {/* Saved Leagues */}
      {leagues.length > 0 && (
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-white mb-4 uppercase">
            YOUR LEAGUES
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leagues.map((league) => (
              <div
                key={league.league_id}
                className="team-card border border-[#262626] bg-[#171717] p-5 cursor-pointer"
                data-testid={`league-card-${league.league_id}`}
                onClick={() => loadLeagueData(league.league_id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-xl text-white tracking-tight uppercase">
                      {league.name}
                    </h3>
                    <p className="text-xs font-mono text-[#a3a3a3] mt-1">
                      {league.scoring_type} / {league.total_rosters} Teams
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeLeague(league.league_id); }}
                    className="text-[#666] hover:text-[#ff3b30] transition-colors p-1"
                    data-testid={`delete-league-${league.league_id}`}
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-[#666]">ID: {league.league_id}</span>
                  <div className="flex items-center gap-1 text-[#ccff00] text-xs font-semibold uppercase">
                    OPEN <ArrowRight size={12} weight="bold" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {leagues.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[#262626]" data-testid="no-leagues-state">
          <Football size={48} weight="thin" className="mx-auto text-[#333] mb-4" />
          <p className="text-[#a3a3a3] text-sm">No leagues imported yet. Enter your Sleeper league ID above to get started.</p>
        </div>
      )}
    </div>
  );
}
